import React, { useState, useRef, useEffect } from 'react';

export type ViewType = 'import' | 'matching' | 'availability' | 'emails' | 'duplicates';

interface MenuItem {
  id: ViewType;
  label: string;
  icon: string;
  disabled?: boolean;
}

interface HamburgerMenuProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  studentsCount: number;
  familiesCount: number;
}

export const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  currentView,
  onViewChange,
  studentsCount,
  familiesCount
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const menuItems: MenuItem[] = [
    { id: 'import', label: 'Import Data', icon: '📤' },
    { id: 'availability', label: 'Family Availability', icon: '📅', disabled: familiesCount === 0 },
    { id: 'duplicates', label: 'Find Duplicates', icon: '🔍', disabled: studentsCount === 0 },
    { id: 'emails', label: 'Email Management', icon: '📧', disabled: studentsCount === 0 || familiesCount === 0 },
    { id: 'matching', label: 'Matching Dashboard', icon: '🤝', disabled: studentsCount === 0 || familiesCount === 0 }
  ];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMenuItemClick = (viewId: ViewType) => {
    onViewChange(viewId);
    setIsOpen(false);
  };

  const getCurrentMenuItem = () => {
    return menuItems.find(item => item.id === currentView);
  };

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 15px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '0.95em',
          fontWeight: '500',
          minWidth: '180px',
          justifyContent: 'space-between',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#0056b3';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#007bff';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{getCurrentMenuItem()?.icon}</span>
          <span>{getCurrentMenuItem()?.label}</span>
        </div>
        
        {/* Hamburger Icon */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          transition: 'transform 0.2s ease',
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
        }}>
          <div style={{
            width: '14px',
            height: '2px',
            backgroundColor: 'white',
            borderRadius: '1px',
            transition: 'all 0.2s ease',
            transform: isOpen ? 'rotate(45deg) translate(3px, 3px)' : 'none'
          }}></div>
          <div style={{
            width: '14px',
            height: '2px',
            backgroundColor: 'white',
            borderRadius: '1px',
            transition: 'all 0.2s ease',
            opacity: isOpen ? 0 : 1
          }}></div>
          <div style={{
            width: '14px',
            height: '2px',
            backgroundColor: 'white',
            borderRadius: '1px',
            transition: 'all 0.2s ease',
            transform: isOpen ? 'rotate(-45deg) translate(3px, -3px)' : 'none'
          }}></div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '0',
          minWidth: '220px',
          backgroundColor: '#2a2a2a',
          border: '1px solid #444',
          borderRadius: '6px',
          marginTop: '5px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          animation: 'slideDown 0.2s ease-out'
        }}>
          <style>
            {`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}
          </style>
          
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuItemClick(item.id)}
              disabled={item.disabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '12px 16px',
                backgroundColor: item.id === currentView ? '#007bff' : 'transparent',
                color: item.disabled ? '#666' : 'white',
                border: 'none',
                borderBottom: '1px solid #444',
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                fontSize: '0.9em',
                textAlign: 'left',
                transition: 'background-color 0.2s ease',
                borderRadius: '0'
              }}
              onMouseEnter={(e) => {
                if (!item.disabled && item.id !== currentView) {
                  e.currentTarget.style.backgroundColor = '#333';
                }
              }}
              onMouseLeave={(e) => {
                if (!item.disabled && item.id !== currentView) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ 
                fontSize: '1.1em',
                opacity: item.disabled ? 0.5 : 1 
              }}>
                {item.icon}
              </span>
              <span style={{ opacity: item.disabled ? 0.5 : 1 }}>
                {item.label}
              </span>
              {item.id === currentView && (
                <span style={{ 
                  marginLeft: 'auto', 
                  fontSize: '0.8em',
                  color: '#b3d9ff'
                }}>
                  ✓
                </span>
              )}
            </button>
          ))}
          
          {/* Footer with counts */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #444',
            fontSize: '0.8em',
            color: '#888',
            backgroundColor: '#1a1a1a'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Students: {studentsCount}</span>
              <span>Families: {familiesCount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};